import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, Button, TextField, MenuItem,
  CircularProgress, Chip, Divider, useMediaQuery, useTheme
} from '@mui/material';
import { ArrowBack, Add, Delete } from '@mui/icons-material';
import axios from 'axios';
import AttachmentsPanel from './AttachmentsPanel';
import { useLocations } from '../hooks/useLocations';

const emptyLine = () => ({
  _key:        Date.now() + Math.random(),
  itemNo:      '',
  description: '',
  qty:         ''
});

const StoreRequisitionForm = () => {
  const navigate    = useNavigate();
  const { reqNo }   = useParams();
  const isEdit      = Boolean(reqNo);

  const [header, setHeader] = useState({
    requisitionDate:  new Date().toISOString().split('T')[0],
    fromLocationCode: '',
    requestedBy:      '',
    department:       '',
    description:      ''
  });
  const [lines,    setLines]    = useState([emptyLine()]);
  const [items,    setItems]    = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [error,    setError]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [status,   setStatus]   = useState('Open');

  const { locations } = useLocations();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const headers  = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    const load = async () => {
      try {
        const [itemsRes, stockRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/payables/items`,      { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/payables/item-stock`, { headers })
        ]);
        setItems(itemsRes.data.filter(i => i.itemType === 'Item'));
        setStockMap(stockRes.data);

        if (isEdit) {
          const { data } = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/payables/requisitions/${reqNo}`,
            { headers }
          );
          setHeader({
            requisitionDate:  data.requisitionDate ? data.requisitionDate.split('T')[0] : '',
            fromLocationCode: data.fromLocationCode || '',
            requestedBy:      data.requestedBy  || '',
            department:       data.department   || '',
            description:      data.description  || ''
          });
          setLines(data.lines.map(l => ({
            _key: Date.now() + Math.random(),
            itemNo: l.itemNo, description: l.description, qty: l.qty
          })));
          setStatus(data.status || 'Open');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reqNo]); // eslint-disable-line

  // Stock of an item at the selected From Location
  const stockAtLocation = (itemNo) => {
    const s = stockMap[itemNo];
    if (!s || !header.fromLocationCode) return null;
    return s.byLocation?.[header.fromLocationCode] || { qty: 0, value: 0 };
  };

  const itemUom = (itemNo) => items.find(i => i.itemNo === itemNo)?.unitOfMeasure || '';

  const updateLine = (key, field, value) => {
    setLines(prev => prev.map(l => {
      if (l._key !== key) return l;
      const updated = { ...l, [field]: value };
      if (field === 'itemNo') {
        const item = items.find(i => i.itemNo === value);
        updated.description = item ? item.itemName : '';
      }
      return updated;
    }));
  };

  const addLine    = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (key) => setLines(prev => prev.filter(l => l._key !== key));

  const handleSave = async () => {
    setError('');
    if (!header.fromLocationCode) { setError('Issue From Location is required'); return; }
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.itemNo)                        { setError(`Line ${i + 1}: Item is required`);      return; }
      if (!l.qty || parseFloat(l.qty) <= 0) { setError(`Line ${i + 1}: Quantity must be > 0`);  return; }
    }

    setSaving(true);
    try {
      // Only Item + Qty are sent — the backend derives cost and GL accounts.
      const payload = {
        ...header,
        lines: lines.map(l => ({
          itemNo:      l.itemNo,
          description: l.description,
          qty:         parseFloat(l.qty)
        }))
      };

      if (isEdit) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/payables/requisitions/${reqNo}`,
          payload, { headers }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/payables/requisitions`,
          payload, { headers }
        );
      }
      navigate('/payables/requisitions');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save requisition');
    } finally {
      setSaving(false);
    }
  };

  const isReadOnly = isEdit && status !== 'Open';

  if (loading) return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  );

  const locName = (code) => locations.find(l => l.locationCode === code)?.locationName || code;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: { xs: 1, sm: 4 }, px: { xs: 1, sm: 0 } }}>
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box display="flex" alignItems="center" mb={3}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/payables/requisitions')} sx={{ mr: 2 }}>
              Back
            </Button>
            <Typography variant="h5" color="primary">
              {isEdit ? `Edit Requisition: ${reqNo}` : 'New Store Requisition'}
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {isReadOnly && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This requisition is <strong>{status}</strong> and cannot be edited.
            </Alert>
          )}
          {isEdit && (
            <Box mb={3}>
              <AttachmentsPanel refType="StoreRequisition" refId={reqNo} />
            </Box>
          )}

          {/* Header */}
          <Box display="flex" flexWrap="wrap" gap={{ xs: 1.5, sm: 2 }} mb={3}>
            <TextField label="Requisition Date" type="date" size="small"
              value={header.requisitionDate}
              onChange={e => setHeader({ ...header, requisitionDate: e.target.value })}
              disabled={isReadOnly}
              InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 180 } }} />

            <TextField select label="Issue From Location" size="small"
              value={header.fromLocationCode}
              onChange={e => setHeader({ ...header, fromLocationCode: e.target.value })}
              disabled={isReadOnly}
              sx={{ width: { xs: '100%', sm: 230 } }}
              helperText="Stock will be drawn from this location">
              <MenuItem value=""><em>Select Location</em></MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc.locationCode} value={loc.locationCode}>
                  {loc.locationCode} – {loc.locationName}
                </MenuItem>
              ))}
            </TextField>

            <TextField label="Requested By" size="small"
              value={header.requestedBy}
              onChange={e => setHeader({ ...header, requestedBy: e.target.value })}
              disabled={isReadOnly}
              sx={{ width: { xs: '100%', sm: 180 } }} />
            <TextField label="Department" size="small"
              value={header.department}
              onChange={e => setHeader({ ...header, department: e.target.value })}
              disabled={isReadOnly}
              sx={{ width: { xs: '100%', sm: 180 } }} />
            <TextField label="Description / Purpose" size="small"
              value={header.description}
              onChange={e => setHeader({ ...header, description: e.target.value })}
              disabled={isReadOnly}
              sx={{ width: { xs: '100%', sm: 260 }, flex: { sm: 1 } }} />
          </Box>

          {/* Lines — table on desktop, stacked cards on mobile */}
          {isMobile ? (
            <Box display="flex" flexDirection="column" gap={1.5} mb={2}>
              {lines.map((line, idx) => {
                const locStock = stockAtLocation(line.itemNo);
                const availQty = locStock ? locStock.qty : null;
                const overDraw = availQty !== null && parseFloat(line.qty) > availQty;
                return (
                  <Card key={line._key} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          Item {idx + 1}
                        </Typography>
                        {!isReadOnly && lines.length > 1 && (
                          <IconButton size="small" color="error" onClick={() => removeLine(line._key)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <TextField select size="small" label="Item" value={line.itemNo} fullWidth
                        disabled={isReadOnly} sx={{ mb: 1.2 }}
                        onChange={e => updateLine(line._key, 'itemNo', e.target.value)}>
                        <MenuItem value=""><em>Select Item</em></MenuItem>
                        {items.map(i => (
                          <MenuItem key={i.itemNo} value={i.itemNo}>
                            {i.itemNo} – {i.itemName}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField size="small" label="Description" value={line.description} fullWidth
                        disabled={isReadOnly} sx={{ mb: 1.2 }}
                        onChange={e => updateLine(line._key, 'description', e.target.value)} />
                      <Box display="flex" gap={1.5} alignItems="flex-start">
                        <TextField size="small" label="Qty" type="number" value={line.qty}
                          disabled={isReadOnly} error={overDraw}
                          helperText={overDraw ? 'Exceeds stock' : ''}
                          inputProps={{ min: 0.001, step: '0.001' }}
                          onChange={e => updateLine(line._key, 'qty', e.target.value)}
                          sx={{ flex: 1 }} />
                        <Box sx={{ flex: 1, pt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {header.fromLocationCode ? `In stock @ ${locName(header.fromLocationCode)}` : 'In stock'}
                          </Typography>
                          {line.itemNo && availQty !== null ? (
                            <Chip
                              label={`${availQty.toFixed(2)}${itemUom(line.itemNo) ? ' ' + itemUom(line.itemNo) : ''}`}
                              color={availQty > 0 ? 'success' : 'error'}
                              size="small" variant="outlined" sx={{ fontWeight: 600, mt: 0.3 }} />
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              {line.itemNo ? (header.fromLocationCode ? '0.00' : 'pick location') : '—'}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ mb: 2, minWidth: 640 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ width: '34%' }}><strong>Item</strong></TableCell>
                <TableCell sx={{ width: '30%' }}><strong>Description</strong></TableCell>
                <TableCell align="right">
                  <strong>
                    {header.fromLocationCode
                      ? `In Stock @ ${locName(header.fromLocationCode)}`
                      : 'In Stock'}
                  </strong>
                </TableCell>
                <TableCell align="right"><strong>Qty</strong></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map(line => {
                const locStock = stockAtLocation(line.itemNo);
                const availQty = locStock ? locStock.qty : null;
                const overDraw = availQty !== null && parseFloat(line.qty) > availQty;
                return (
                  <TableRow key={line._key}>
                    <TableCell>
                      <TextField select size="small" value={line.itemNo} fullWidth
                        disabled={isReadOnly}
                        onChange={e => updateLine(line._key, 'itemNo', e.target.value)}>
                        <MenuItem value=""><em>Select Item</em></MenuItem>
                        {items.map(i => (
                          <MenuItem key={i.itemNo} value={i.itemNo}>
                            {i.itemNo} – {i.itemName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={line.description} fullWidth
                        disabled={isReadOnly}
                        onChange={e => updateLine(line._key, 'description', e.target.value)} />
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 110 }}>
                      {line.itemNo && availQty !== null ? (
                        <Chip
                          label={`${availQty.toFixed(2)}${itemUom(line.itemNo) ? ' ' + itemUom(line.itemNo) : ''}`}
                          color={availQty > 0 ? 'success' : 'error'}
                          size="small" variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      ) : line.itemNo ? (
                        <Typography variant="caption" color="text.disabled">
                          {header.fromLocationCode ? '0.00' : 'pick location'}
                        </Typography>
                      ) : ''}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <TextField size="small" type="number" value={line.qty} fullWidth
                        disabled={isReadOnly}
                        error={overDraw}
                        helperText={overDraw ? 'Exceeds stock' : ''}
                        inputProps={{ min: 0.001, step: '0.001' }}
                        onChange={e => updateLine(line._key, 'qty', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      {!isReadOnly && lines.length > 1 && (
                        <IconButton size="small" color="error" onClick={() => removeLine(line._key)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </Box>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            {!isReadOnly && (
              <Button startIcon={<Add />} onClick={addLine} variant="outlined" size="small"
                fullWidth={isMobile}>
                Add Line
              </Button>
            )}
            <Typography variant="body2" color="text.secondary" ml="auto" noWrap>
              {lines.filter(l => l.itemNo).length} item{lines.filter(l => l.itemNo).length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          <Divider sx={{ my: 2, display: { xs: 'block', sm: 'none' } }} />

          <Box display="flex" flexDirection={{ xs: 'column-reverse', sm: 'row' }}
            justifyContent="flex-end" mt={{ xs: 0, sm: 3 }} gap={{ xs: 1, sm: 2 }}>
            <Button onClick={() => navigate('/payables/requisitions')} fullWidth={isMobile}>
              {isReadOnly ? 'Back' : 'Cancel'}
            </Button>
            {!isReadOnly && (
              <Button variant="contained" onClick={handleSave} disabled={saving} fullWidth={isMobile}>
                {saving ? 'Saving...' : 'Save Requisition'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StoreRequisitionForm;
